import { Loader } from "@mantine/core";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { US, AU, GB, NZ, JP, CA, FR } from "country-flag-icons/react/3x2";

import {
  IconAlertCircleFilled,
  IconFileTypeCsv,
  IconFileTypePdf,
  IconFileDescription,
  IconDatabase,
  IconFileTypeSql,
  IconFileTypeXml,
  IconFileTypeZip,
  IconCheck,
  IconCircleCheckFilled,
} from "@tabler/icons-react";

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

export const HEADER_HEIGHT = 60;

export const SINGLE_DEFECT_MODE =
  import.meta.env.VITE_SINGLE_DEFECT_MODE === "true";

// "true" offers the simplified dashboard and the toggle for switching between the two.
export const SIMPLE_DASHBOARD_ENABLED =
  import.meta.env.VITE_SIMPLE_DASHBOARD === "true";

// Date the Microsoft SSO cut-over takes effect, shown in the link-account banner as eg. "Aug 3rd 2026".
const cutoverDate = dayjs(
  (import.meta.env.VITE_SSO_CUTOVER_DATE || "").trim(),
  "YYYY-MM-DD",
  true,
);

// Null when VITE_SSO_CUTOVER_DATE is unset or not a valid ISO date, which drops the date from the banner text.
export const SSO_CUTOVER_DATE_LABEL = cutoverDate.isValid()
  ? cutoverDate.format("MMM Do YYYY")
  : null;

export const SEVERITY_COLORS = {
  high: "var(--mantine-color-red-8)",
  medium: "var(--mantine-color-orange-7)",
  low: "var(--mantine-color-yellow-4)",
  none: "var(--mantine-color-teal-6)",
};

export const NO_RATING_COLOR = "var(--mantine-color-gray-5)";

export const RATING_TO_SEVERITY = {
  1: "none",
  2: "none",
  3: "low",
  4: "medium",
  5: "high",
};

export const PIPE_TYPE_LABELS = {
  sewer: "Sewer",
  stormwater: "Stormwater",
  SS: "Sewer",
  SW: "Stormwater",
};

export const STANDARDS = [
  { value: "1", label: "Australia 2013", flag: AU },
  { value: "6", label: "Australia 2020", flag: AU },
  { value: "2", label: "UK V5", flag: GB },
  { value: "4", label: "NZ V3", flag: NZ },
  { value: "5", label: "NZ V4", flag: NZ },
  { value: "3", label: "US V7", flag: US },
  { value: "7", label: "US V8", flag: US },
  { value: "8", label: "Japan VS", flag: JP },
];

// True for the AU and NZ standards, which do not enforce required observation fields.
export const isAuNzStandard = (standardLabel) => {
  const match = STANDARDS.find((s) => s.label === standardLabel);
  return match?.flag === AU || match?.flag === NZ;
};

export const OrgTypes = {
  CONTRACTOR: "Contractor",
  ASSET_OWNER: "Asset_Owner",
};

export const UserLevels = {
  STANDARD: "standard",
  UPLOAD_ONLY: "upload only",
};

export const UPLOAD_STATUSES = {
  QUEUED: {
    value: "Queued",
    label: "Queued",
  },
  UPLOADING: {
    value: "Uploading",
    label: "Uploading...",
  },
  UPLOAD_FAILED: {
    value: "Upload Failed",
    label: "Upload failed",
  },
  MOVING_TO_PROCESSING: {
    value: "Moving To Processing",
    label: "Moving to processing...",
  },
  PROCESSING: {
    value: "Processing",
    label: "Processing",
  },
  ANALYSING_VIDEO: {
    value: "Analysing Video",
    label: "Analysing video",
  },
  STORING_RESULTS: {
    value: "Storing Results",
    label: "Storing results",
  },
  WAITING_TO_PROCESS: {
    value: "Waiting to process",
    label: "Waiting to process",
  },
  FAILED_TO_PROCESS: {
    value: "Failed To Process",
    label: "Failed to process",
  },
  CANCELLING: {
    value: "Cancelling",
    label: "Cancelling...",
  },
  COMPLETE: {
    value: "Complete",
    label: "Upload Complete",
  },
};

export const UPLOAD_CONTAINER_NAME =
  import.meta.env.VITE_UPLOAD_CONTAINER_NAME || "uploadedvideofiles";

export const ExportStatus = [
  {
    value: "PE",
    label: "Pending",
    Icon: Loader,
    iconProps: { size: 9 },
    color: "teal",
  },
  {
    value: "PR",
    label: "Processing",
    Icon: Loader,
    iconProps: { size: 9 },
    color: "teal",
  },
  {
    value: "CO",
    label: "Complete",
    Icon: IconCircleCheckFilled,
    iconProps: { size: 13 },
    color: "green",
  },
  {
    value: "FA",
    label: "Failed",
    Icon: IconAlertCircleFilled,
    iconProps: { size: 13 },
    color: "red",
  },
  {
    value: "DL",
    label: "Downloaded",
    Icon: IconCheck,
    iconProps: { size: 13 },
    color: "gray",
  },
];

export const getExportStatus = (value) =>
  ExportStatus.find((status) => status.value === value);

export const ExportTypes = [
  {
    name: "RR",
    Icon: IconFileTypeCsv,
    iconProps: {},
    label: "Repair Recommendations",
  },
  {
    name: "BI",
    Icon: IconFileTypePdf,
    iconProps: {},
    label: "Bulk Inspection",
  },
  { name: "AS", Icon: IconFileTypeCsv, iconProps: {}, label: "Inspection" },
  { name: "DF", Icon: IconFileTypeCsv, iconProps: {}, label: "Defect" },
  {
    name: "IP",
    Icon: IconFileTypeCsv,
    iconProps: {},
    label: "Inspection Plan",
  },
  { name: "PR", Icon: IconFileDescription, iconProps: {}, label: "Predictor" },
  { name: "IA", Icon: IconFileDescription, iconProps: {}, label: "InfoAsset" },
  { name: "PM", Icon: IconDatabase, iconProps: {}, label: "AccessDB" },
  { name: "PS", Icon: IconFileTypeSql, iconProps: {}, label: "PACP8" },
];

export const ExportFormats = [
  { name: "PDF", label: "PDF", Icon: IconFileTypePdf, iconProps: {} },
  { name: "CSV", label: "CSV", Icon: IconFileTypeCsv, iconProps: {} },
  { name: "XML", label: "XML", Icon: IconFileTypeXml, iconProps: {} },
  { name: "ZIP", label: "ZIP", Icon: IconFileTypeZip, iconProps: {} },
  { name: "MDB", label: "MDB", Icon: IconDatabase, iconProps: {} },
  { name: "SQL", label: "SQL", Icon: IconFileTypeSql, iconProps: {} },
];

export const fileSizeToString = (size) => {
  if (size < 1024) {
    return `${size} bytes`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  } else if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  } else {
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
};

export const timeAgo = (date) => {
  const now = new Date();
  const secondsPast = Math.floor((now - new Date(date)) / 1000);

  if (secondsPast < 60) {
    return "just now";
  }
  if (secondsPast < 3600) {
    // 60 * 60
    const minutes = Math.floor(secondsPast / 60);
    return `${minutes} min${minutes > 1 ? "s" : ""} ago`;
  }
  if (secondsPast < 86400) {
    // 60 * 60 * 24
    const hours = Math.floor(secondsPast / 3600);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  if (secondsPast < 2592000) {
    // 60 * 60 * 24 * 30
    const days = Math.floor(secondsPast / 86400);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }
  if (secondsPast < 31536000) {
    // 60 * 60 * 24 * 365
    const months = Math.floor(secondsPast / 2592000);
    return `${months} month${months > 1 ? "s" : ""} ago`;
  }
  const years = Math.floor(secondsPast / 31536000);
  return `${years} year${years > 1 ? "s" : ""} ago`;
};

/** Formats a duration in milliseconds as m:ss, or h:mm:ss past an hour. */
export const millisecondsToTimestamp = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.floor((milliseconds || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = String(totalSeconds % 60).padStart(2, "0");

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${seconds}`;
  }
  return `${minutes}:${seconds}`;
};

export const generateExportName = (exportObj) => {
  const typeObj = ExportTypes.find((type) => type.name === exportObj.type);
  const formatObj = ExportFormats.find(
    (format) => format.name === exportObj.format,
  );
  if (exportObj.fileDisplayNames && exportObj.fileDisplayNames[0]) {
    return exportObj.fileDisplayNames[0];
  }
  return `${typeObj ? typeObj.label : "Unknown"} ${
    formatObj ? formatObj.label : "Unknown"
  } Export - ${new Date(exportObj.createdAt).toLocaleString()}`;
};

export const Countries = {
  GB: "GB",
  US: "US",
  AU: "AU",
  NZ: "NZ",
};

export const FlagMap = {
  AU: AU,
  US: US,
  GB: GB,
  UK: GB,
  NZ: NZ,
  JP: JP,
  CA: CA,
  FR: FR,
};

export const ProcessingStatusReasonLabels = {
  GE: "Generic Error",
  VE: "Validation Error",
  FC: "File Corrupted",
  TW: "Timeout While Waiting",
  TP: "Timeout While Processing",
  TU: "Timeout While Uploading",
};
